import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';

const TYPES = [
  { v: 'DESIGN_SYSTEM', l: 'Design system' },
  { v: 'PROTOTYPE',     l: 'Prototipo' },
  { v: 'BRAND_ASSET',   l: 'Asset brand' },
  { v: 'SOCIAL_ASSET',  l: 'Asset social' },
  { v: 'WEB_PAGE',      l: 'Pagina web' },
  { v: 'PRESENTATION',  l: 'Presentazione' },
  { v: 'OTHER',         l: 'Altro' },
] as const;

const STATUSES = ['BRIEF', 'DRAFT', 'REVIEW', 'APPROVED', 'DELIVERED'] as const;

export function NewDeliverableModal({ onClose, projectId }: { onClose: () => void; projectId?: string }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    projectId: projectId ?? '',
    title: '',
    description: '',
    type: 'BRAND_ASSET' as typeof TYPES[number]['v'],
    status: 'DRAFT' as typeof STATUSES[number],
    ownerId: '',
    deadline: '',
    figmaUrl: '',
    previewUrl: '',
    notes: '',
  });

  const { data: projects } = useQuery({
    queryKey: ['projects-design'],
    queryFn: () => api.get<{ items: Array<{ id: string; code: string; name: string }> }>('/projects?division=DESIGN&pageSize=200'),
    enabled: !projectId,
  });
  const { data: users } = useQuery({
    queryKey: ['users-lite'],
    queryFn: () => api.get<{ items: Array<{ id: string; firstName: string; lastName: string }> }>('/users?pageSize=200'),
  });

  const create = useMutation({
    mutationFn: () => api.post('/deliverables', {
      projectId: form.projectId || null,
      title: form.title,
      description: form.description || null,
      type: form.type,
      status: form.status,
      ownerId: form.ownerId || null,
      deadline: form.deadline || null,
      figmaUrl: form.figmaUrl || null,
      previewUrl: form.previewUrl || null,
      notes: form.notes || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deliverables'] });
      toast.ok('Deliverable creato', form.title);
      onClose();
    },
    onError: (err) => toast.alert('Errore creazione deliverable', err instanceof ApiError ? err.message : 'Riprova'),
  });

  return (
    <Modal
      title="Nuovo deliverable"
      kicker="Digital Design · Output"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => create.mutate()} disabled={!form.title || create.isPending}>
            Crea <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Titolo" required span={12}>
          <input className="inp" placeholder="es. Brand identity primaria — logo & wordmark" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </Field>
        <Field label="Descrizione" span={12}>
          <textarea className="inp" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <Field label="Tipo" span={6}>
          <select className="inp" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as any })}>
            {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
        </Field>
        <Field label="Stato" span={6}>
          <div className="seg-control">
            {STATUSES.map((s) => (
              <button key={s} aria-pressed={form.status === s} onClick={() => setForm({ ...form, status: s })}>{s}</button>
            ))}
          </div>
        </Field>
        {!projectId && (
          <Field label="Progetto" span={6}>
            <select className="inp" value={form.projectId} onChange={(e) => setForm({ ...form, projectId: e.target.value })}>
              <option value="">— Indipendente —</option>
              {(projects?.items ?? []).map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
            </select>
          </Field>
        )}
        <Field label="Owner" span={projectId ? 6 : 6}>
          <select className="inp" value={form.ownerId} onChange={(e) => setForm({ ...form, ownerId: e.target.value })}>
            <option value="">—</option>
            {(users?.items ?? []).map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
          </select>
        </Field>
        <Field label="Deadline" span={12}>
          <input className="inp mono" type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
        </Field>
        <Field label="Link Figma" span={6}>
          <input className="inp" placeholder="https://figma.com/file/..." value={form.figmaUrl} onChange={(e) => setForm({ ...form, figmaUrl: e.target.value })} />
        </Field>
        <Field label="Link preview" span={6}>
          <input className="inp" placeholder="https://..." value={form.previewUrl} onChange={(e) => setForm({ ...form, previewUrl: e.target.value })} />
        </Field>
        <Field label="Note interne" span={12}>
          <textarea className="inp" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}
